import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { ENV_VARIABLES } from 'src/common/constants';
import {
  getPlaylistUrl,
  testVideoSource,
} from 'src/components/utils/video-streaming-utils';

export type VideoStreamContextType = {
  probeVideoSource: (
    moduleId: string,
  ) => Promise<{ playlistUrl: string } | undefined>;
  setStreamSource: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  streamSources: Record<string, boolean>;
  viewerToken: string;
};
export const VideoStreamContext = createContext<VideoStreamContextType>(
  {} as VideoStreamContextType,
);

export const VideoStreamProvider = (props: PropsWithChildren) => {
  const {
    STREAMING_SERVER_URL: remoteServerUrl,
    STREAMING_SERVER_VIEWER_TOKEN: viewerToken,
  } = ENV_VARIABLES;
  const [streamSource, setStreamSource] = useState<Record<string, boolean>>({});
  const probeVideoSource = useCallback(
    async (moduleId: string) => {
      const remoteServerPlaylistUrl = getPlaylistUrl(
        remoteServerUrl,
        moduleId,
        viewerToken,
        false,
      );
      const isRemoteServerAvailable = await testVideoSource(
        remoteServerPlaylistUrl,
      );
      if (isRemoteServerAvailable) {
        setStreamSource((prev) => ({
          ...prev,
          [moduleId]: true,
        }));
        return {
          playlistUrl: remoteServerPlaylistUrl,
        };
      }
      setStreamSource((prev) => {
        if (!(moduleId in prev)) return prev;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [moduleId]: _, ...newSources } = prev;
        return newSources;
      });
    },
    [remoteServerUrl, viewerToken],
  );
  return (
    <VideoStreamContext.Provider
      value={{
        probeVideoSource,
        setStreamSource,
        streamSources: streamSource,
        viewerToken,
      }}
    >
      {props.children}
    </VideoStreamContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoStreamContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};
